/**
 * GrandRoundsFlowDiagram Component
 *
 * Main flow diagram container showing the complete data flow through a Grand Rounds session.
 * Displays input data, each debate round, and final synthesis with expandable nodes.
 */

import { useState } from 'react';
import { clsx } from 'clsx';
import { Globe } from 'lucide-react';
import { FlowNode, InputDataNode, type FlowNodeType } from './FlowNode';
import { getModelName } from '../../../components/ModelLogo';
import { VerticalConnector } from './FlowArrow';
import { FlowLegend, InlineLegend } from './FlowLegend';
import { ConvergenceMetricsDisplay, ConvergenceBadge } from './ConvergenceMetricsDisplay';
import type {
  GrandRoundsSessionDetail,
  DebateRound,
  AgentContribution,
  ContributionType,
  ConvergenceMetrics,
} from '../../../lib/api';

interface GrandRoundsFlowDiagramProps {
  session: GrandRoundsSessionDetail;
}

// Map contribution type to flow node type
function mapContributionType(type: ContributionType): FlowNodeType {
  const mapping: Record<ContributionType, FlowNodeType> = {
    PROPOSAL: 'proposal',
    CRITIQUE: 'critique',
    DEFENSE: 'defense',
    CONCESSION: 'defense',
    CLARIFICATION: 'defense',
    SYNTHESIS: 'synthesis',
  };
  return mapping[type] || 'data';
}

// Get round type label - different for Phase 1 vs Phase 2
function getRoundTypeLabel(roundType: string, phase?: string): string {
  if (phase === 'PHASE_2_REFERRALS') {
    const phase2Labels: Record<string, string> = {
      INITIAL: 'Care Provider Matching & Discovery',
      PEER_REVIEW: 'Peer Review of Providers & Rankings',
      RESPONSE: 'Response & Updated Rankings',
      CLARIFICATION: 'Clarification',
      MODERATOR_SYNTHESIS: 'Final Provider Consensus',
    };
    return phase2Labels[roundType] || roundType;
  }

  // Phase 1 (default)
  const labels: Record<string, string> = {
    INITIAL: 'Initial Proposals',
    PEER_REVIEW: 'Peer Review',
    RESPONSE: 'Response & Defense',
    CLARIFICATION: 'Clarification',
    MODERATOR_SYNTHESIS: 'Moderator Synthesis',
  };
  return labels[roundType] || roundType;
}

export function GrandRoundsFlowDiagram({ session }: GrandRoundsFlowDiagramProps) {
  // Track which nodes are expanded (only one at a time for clarity)
  const [expandedNodeId, setExpandedNodeId] = useState<string | null>(null);

  const toggleNode = (nodeId: string) => {
    setExpandedNodeId((prev) => (prev === nodeId ? null : nodeId));
  };

  // Extract input data from session (including inputContext from API)
  const inputContext = session.inputContext;
  const hasInputContext = !!inputContext;

  // Group rounds for display
  const debateRounds = session.rounds.filter(
    (r) => r.roundType !== 'MODERATOR_SYNTHESIS'
  );
  const moderatorRound = session.rounds.find(
    (r) => r.roundType === 'MODERATOR_SYNTHESIS'
  );

  return (
    <div className="space-y-6">
      {/* Header with Legend */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Data Flow Diagram</h3>
          <p className="text-sm text-gray-500">
            Click on any node to expand and view full input/output data
          </p>
        </div>
        <InlineLegend />
      </div>

      {/* Flow Diagram */}
      <div className="relative">
        {/* Input Data Section - Row 1: Case & Patient */}
        <div className="mb-4">
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-2 font-medium">
            Case & Patient Information
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <InputDataNode
              title="Test Case"
              data={{
                name: session.testRun.testCase.name,
                condition: session.testRun.testCase.condition,
                phase: session.phase,
              }}
              icon="📋"
              isExpanded={expandedNodeId === 'input-test-case'}
              onToggle={() => toggleNode('input-test-case')}
            />
            <InputDataNode
              title="Patient Info"
              data={inputContext?.patientInfo || session.testRun.testCase.patientInfo || { note: 'No patient info available' }}
              icon="👤"
              isExpanded={expandedNodeId === 'input-patient'}
              onToggle={() => toggleNode('input-patient')}
            />
            <InputDataNode
              title="Configuration"
              data={session.config}
              icon="⚙️"
              isExpanded={expandedNodeId === 'input-config'}
              onToggle={() => toggleNode('input-config')}
            />
          </div>
        </div>

        {/* Input Data Section - Row 2: Questionnaire & Answers */}
        {hasInputContext && (inputContext.questionnaire || inputContext.answers) && (
          <div className="mb-4">
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-2 font-medium">
              Patient Questionnaire & Responses
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {inputContext.questionnaire && (
                <InputDataNode
                  title="Patient Questionnaire"
                  data={inputContext.questionnaire}
                  icon="📝"
                  isExpanded={expandedNodeId === 'input-questionnaire'}
                  onToggle={() => toggleNode('input-questionnaire')}
                />
              )}
              {inputContext.answers && (
                <InputDataNode
                  title="Patient Answers"
                  data={inputContext.answers}
                  icon="✏️"
                  isExpanded={expandedNodeId === 'input-answers'}
                  onToggle={() => toggleNode('input-answers')}
                />
              )}
            </div>
          </div>
        )}

        {/* Input Data Section - Row 3: Documents */}
        {hasInputContext && inputContext.documents && inputContext.documents.length > 0 && (
          <div className="mb-4">
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-2 font-medium">
              Uploaded Documents
            </div>
            <div className="grid grid-cols-1 gap-3">
              <InputDataNode
                title={`Documents (${inputContext.documents.length})`}
                data={inputContext.documents}
                icon="📄"
                isExpanded={expandedNodeId === 'input-documents'}
                onToggle={() => toggleNode('input-documents')}
              />
            </div>
          </div>
        )}

        {/* Input Data Section - Row 4: Model Analysis */}
        {hasInputContext && inputContext.modelAnalysis && inputContext.modelAnalysis.length > 0 && (
          <div className="mb-4">
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-2 font-medium">
              {session.phase === 'PHASE_2_REFERRALS'
                ? 'Phase 1 Consensus (Matching Criteria Input)'
                : 'Model Analysis (Input to Debate)'}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {inputContext.modelAnalysis.map((analysis) => (
                <InputDataNode
                  key={analysis.executionId}
                  title={session.phase === 'PHASE_2_REFERRALS'
                    ? `${getModelName(analysis.model)} Matching Criteria`
                    : `${getModelName(analysis.model)} Analysis`}
                  data={{
                    indication: analysis.indicationAnalysis,
                    criteria: analysis.criteriaAnalysis,
                  }}
                  icon={session.phase === 'PHASE_2_REFERRALS' ? '📋' : '🔬'}
                  isExpanded={expandedNodeId === `input-analysis-${analysis.executionId}`}
                  onToggle={() => toggleNode(`input-analysis-${analysis.executionId}`)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Input Data Section - Row 5: Search Results (Phase 2 reference data) */}
        {hasInputContext && inputContext.searchAnalysis && inputContext.searchAnalysis.length > 0 && (
          <div className="mb-4">
            <div className="text-xs uppercase tracking-wide text-gray-500 mb-2 font-medium">
              Phase 1 Search Results (Reference Only)
            </div>
            <p className="text-xs text-gray-400 mb-2 italic">
              Note: In Phase 2, each expert performs their own web searches to find providers
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {inputContext.searchAnalysis.map((search) => (
                <InputDataNode
                  key={`search-${search.model}`}
                  title={`${getModelName(search.model)} Providers (Reference)`}
                  data={search.searchExecutions}
                  icon="📚"
                  isExpanded={expandedNodeId === `input-search-${search.model}`}
                  onToggle={() => toggleNode(`input-search-${search.model}`)}
                />
              ))}
            </div>
          </div>
        )}

        {/* Connector to first round */}
        <VerticalConnector label={session.phase === 'PHASE_2_REFERRALS' ? 'Experts Begin Matching' : 'Start Debate'} />

        {/* Debate Rounds */}
        {debateRounds.map((round, roundIndex) => (
          <div key={round.id}>
            {/* Round Header */}
            <RoundSection
              round={round}
              roundIndex={roundIndex}
              phase={session.phase}
              previousMetrics={roundIndex > 0 ? debateRounds[roundIndex - 1].convergenceMetrics : undefined}
              expandedNodeId={expandedNodeId}
              onToggleNode={toggleNode}
            />

            {/* Connector to next round or moderator */}
            {(roundIndex < debateRounds.length - 1 || moderatorRound) && (
              <VerticalConnector
                label={
                  roundIndex === debateRounds.length - 1
                    ? (session.phase === 'PHASE_2_REFERRALS' ? 'Final Provider Ranking' : 'Moderator Review')
                    : `Round ${roundIndex + 2}`
                }
                highlighted={roundIndex === debateRounds.length - 1}
              />
            )}
          </div>
        ))}

        {/* Moderator Synthesis */}
        {moderatorRound && (
          <ModeratorSection
            round={moderatorRound}
            session={session}
            expandedNodeId={expandedNodeId}
            onToggleNode={toggleNode}
          />
        )}

        {/* Final Output Section */}
        {session.status === 'COMPLETED' && (
          <>
            <VerticalConnector
              label={session.phase === 'PHASE_2_REFERRALS' ? 'Final Recommendations' : 'Final Output'}
              highlighted
            />
            <FinalOutputSection
              session={session}
              expandedNodeId={expandedNodeId}
              onToggleNode={toggleNode}
            />
          </>
        )}
      </div>

      {/* Full Legend at bottom */}
      <FlowLegend showModels={true} showContributionTypes={true} compact={false} />
    </div>
  );
}

/**
 * Section for a single debate round
 */
function RoundSection({
  round,
  roundIndex: _roundIndex, // Passed for potential future use
  phase,
  previousMetrics,
  expandedNodeId,
  onToggleNode,
}: {
  round: DebateRound;
  roundIndex: number;
  phase: string;
  previousMetrics?: ConvergenceMetrics;
  expandedNodeId: string | null;
  onToggleNode: (id: string) => void;
}) {
  void _roundIndex; // Silence unused variable warning

  // Phase 2 INITIAL round involves web search
  const isWebSearchRound = phase === 'PHASE_2_REFERRALS' && round.roundType === 'INITIAL';

  return (
    <div className="mb-4">
      {/* Round Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-gray-500 font-medium">
            Round {round.roundNumber}: {getRoundTypeLabel(round.roundType, phase)}
          </span>
          {isWebSearchRound && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
              <Globe className="h-3 w-3" />
              Matching
            </span>
          )}
          <span
            className={clsx(
              'px-2 py-0.5 text-xs rounded-full',
              round.status === 'COMPLETED'
                ? 'bg-green-100 text-green-700'
                : round.status === 'RUNNING'
                  ? 'bg-blue-100 text-blue-700'
                  : round.status === 'FAILED'
                    ? 'bg-red-100 text-red-700'
                    : 'bg-gray-100 text-gray-600'
            )}
          >
            {round.status}
          </span>
        </div>
        {round.convergenceMetrics && (
          <ConvergenceBadge metrics={round.convergenceMetrics} />
        )}
      </div>

      {/* Matching explanation for Phase 2 INITIAL */}
      {isWebSearchRound && (
        <p className="text-xs text-emerald-600 mb-2 italic">
          Each expert independently matches care providers to the matching criteria
        </p>
      )}

      {/* Contributions Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {round.contributions.map((contribution) => (
          <ContributionNode
            key={contribution.id}
            contribution={contribution}
            isExpanded={expandedNodeId === contribution.id}
            onToggle={() => onToggleNode(contribution.id)}
          />
        ))}
      </div>

      {/* Convergence Metrics (if not compact display) */}
      {round.convergenceMetrics && (
        <div className="mt-3">
          <ConvergenceMetricsDisplay
            metrics={round.convergenceMetrics}
            previousMetrics={previousMetrics}
            compact={true}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Node for a single contribution
 */
function ContributionNode({
  contribution,
  isExpanded,
  onToggle,
}: {
  contribution: AgentContribution;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const content = contribution.content as Record<string, unknown>;

  return (
    <FlowNode
      nodeId={contribution.id}
      nodeType={mapContributionType(contribution.contributionType)}
      title={contribution.persona || getModelName(contribution.model)}
      subtitle={contribution.contributionType}
      model={contribution.model}
      isModeratorAgent={contribution.isModeratorAgent}
      contributionType={contribution.contributionType}
      outputData={content}
      prompt={contribution.personaPrompt}
      confidence={contribution.confidence}
      latencyMs={contribution.latencyMs}
      costUsd={contribution.costUsd}
      tokenUsage={contribution.tokenUsage}
      positionChanged={contribution.positionChanged}
      isExpanded={isExpanded}
      onToggle={onToggle}
    />
  );
}

/**
 * Moderator Synthesis Section
 */
function ModeratorSection({
  round,
  session,
  expandedNodeId,
  onToggleNode,
}: {
  round: DebateRound;
  session: GrandRoundsSessionDetail;
  expandedNodeId: string | null;
  onToggleNode: (id: string) => void;
}) {
  const moderatorContribution = round.contributions.find((c) => c.isModeratorAgent);
  const isPhase2 = session.phase === 'PHASE_2_REFERRALS';

  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={clsx(
            'text-xs uppercase tracking-wide font-medium',
            isPhase2 ? 'text-emerald-600' : 'text-indigo-600'
          )}>
            {isPhase2 ? 'Final Provider Consensus' : 'Moderator Synthesis'}
          </span>
          {session.outcomeType && (
            <span
              className={clsx(
                'px-2 py-0.5 text-xs rounded-full font-medium',
                session.outcomeType === 'FULL_CONSENSUS'
                  ? 'bg-green-100 text-green-700'
                  : session.outcomeType === 'MAJORITY_CONSENSUS'
                    ? 'bg-blue-100 text-blue-700'
                    : session.outcomeType === 'PARTIAL_CONSENSUS'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-purple-100 text-purple-700'
              )}
            >
              {session.outcomeType.replace(/_/g, ' ')}
            </span>
          )}
        </div>
      </div>

      {/* Moderator Node (full width) */}
      {moderatorContribution && (
        <div className="max-w-2xl mx-auto">
          <ContributionNode
            contribution={moderatorContribution}
            isExpanded={expandedNodeId === moderatorContribution.id}
            onToggle={() => onToggleNode(moderatorContribution.id)}
          />
        </div>
      )}

      {/* Final Convergence Metrics */}
      {session.finalConvergence && (
        <div className="mt-3 max-w-2xl mx-auto">
          <ConvergenceMetricsDisplay
            metrics={session.finalConvergence}
            compact={false}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Final Output Section showing consensus result or divergent options
 */
function FinalOutputSection({
  session,
  expandedNodeId,
  onToggleNode,
}: {
  session: GrandRoundsSessionDetail;
  expandedNodeId: string | null;
  onToggleNode: (id: string) => void;
}) {
  const isPhase2 = session.phase === 'PHASE_2_REFERRALS';

  return (
    <div className="mb-4">
      <div className={clsx(
        'text-xs uppercase tracking-wide font-medium mb-2',
        isPhase2 ? 'text-emerald-600' : 'text-green-600'
      )}>
        {isPhase2 ? 'Final Provider Recommendations' : 'Final Output'}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Consensus Result */}
        {session.consensusResult && (
          <InputDataNode
            title={isPhase2 ? 'Recommended Providers' : 'Consensus Result'}
            data={session.consensusResult}
            icon={isPhase2 ? '🏥' : '✅'}
            isExpanded={expandedNodeId === 'output-consensus'}
            onToggle={() => onToggleNode('output-consensus')}
          />
        )}

        {/* Divergent Options */}
        {session.divergentOptions && session.divergentOptions.length > 0 && (
          <InputDataNode
            title={isPhase2 ? 'Alternative Provider Lists' : 'Divergent Options'}
            data={session.divergentOptions}
            icon="⚠️"
            isExpanded={expandedNodeId === 'output-divergent'}
            onToggle={() => onToggleNode('output-divergent')}
          />
        )}

        {/* Session Summary */}
        <InputDataNode
          title="Session Summary"
          data={{
            phase: session.phase,
            totalRounds: session.totalRounds,
            totalCostUsd: session.totalCostUsd,
            totalTokens: session.totalTokens,
            outcomeType: session.outcomeType,
            completedAt: session.completedAt,
          }}
          icon="📊"
          isExpanded={expandedNodeId === 'output-summary'}
          onToggle={() => onToggleNode('output-summary')}
        />
      </div>
    </div>
  );
}
